/* global SVP */
'use strict';

import Sizzle from 'sizzle';
import * as domUtils from '../util/dom';
import device from '../util/device';
import debugUtil from '../util/debug';

import * as UIConstants from './ui/ui-constants';

import UIPlayButton from './ui/play-button';
import UILanguageButton from './ui/language-button';
import UIFullscreenButton from './ui/fullscreen-button';
import UIVolumeControls from './ui/volume-controls';
import UITimelineControls from './ui/timeline-controls';

const Debug = debugUtil('ui');

const ClassNames = {
	BUTTON_CONTAINER: 'svp_js-buttons',
    BUTTON_CONTAINER_WAS_SPLASH: 'svp_ui-buttons--was-splash',
    BUTTON_CONTAINER_LEFT: 'svp_ui-buttons__left',
    BUTTON_CONTAINER_LEFT_LIVE: 'svp_ui-buttons__left--live',
    BUTTON_CONTAINER_RIGHT: 'svp_ui-buttons__right',
	BUTTON_CONTAINER_BUTTON: 'svp_ui-buttons__button',
	CONTROLS: 'svp_js-controls',
	PLAY: 'svp_js-controls-btn--play',
	UI_CONTAINER: 'svp_js-ui',
    TOGGLE_TAB_INDEX: 'svp_js-togg-tab-index',
    LOADER: 'svp_js-loader'
};

const FadeIntervals = {
    SHORT: 10,
    MEDIUM: 3000,
    LONG: 10000
};

const STATE_CLASS = UIConstants.STATE_CLASS;
const PLAYING_STATE = UIConstants.PLAYING_STATE;
const NAMESPACE = 'svtUI';

const instances = new Map();

class UIPlugin {
    constructor(api) {
        this.api = api;

        this._isEventsBound = false;
        this._isVideoStarted = false;
        this._isVideoPlaying = false;
        this._isVideoPaused = false;
        this._isVideoBuffering = false;
        this._isControlsVisible = false;
        this._isTouchClick = false;
        this._isTouchDown = false;
        this._isTouchMove = false;
        this._controlsHasFocus = false;
        this._isMouseOver = false;
        this._useExternalUi = false;
        this._prevState = PLAYING_STATE.paused;
        this._uiFadeTimeout = null;
        this._insertedButtons = new Set();

        this.uiPlayButton = UIPlayButton.create(api);
        this.uiTimelineControls = UITimelineControls.create(api);
        this.uiFullscreenButton = UIFullscreenButton.create(api);
        this.uiVolumeControls = UIVolumeControls.create(api);
        this.uiLanguageButton = UILanguageButton.create(api);

        domUtils.addClass(this.uiPlayButton, ClassNames.TOGGLE_TAB_INDEX);
        domUtils.addClass(this.uiTimelineControls, ClassNames.TOGGLE_TAB_INDEX);
        domUtils.addClass(this.uiFullscreenButton, ClassNames.TOGGLE_TAB_INDEX);
        domUtils.addClass(this.uiVolumeControls, ClassNames.TOGGLE_TAB_INDEX);
    }

    _getElement(className) {
        return Sizzle('.' + className, this.api.element())[0];
    }

    _createVideoControls() {
        const controls = domUtils.fromString(`
            <div class="svp_ui svp_js-ui">
                <div class="svp_ui-buttons svp_js-buttons svp_is--initial-ui">
                    <div class="svp_ui-buttons__left"></div>
                    <div class="svp_ui-buttons__right"></div>
                </div>
                <div class="svp_ui-controls-container">
                    <div class="svp_ui-controls svp_js-controls svp_is--initial-ui"></div>
                </div>
                <div class="svp_ui-loader svp_js-loader">
                    <span class="svp_ui-loader__spinner"></span>
                </div>
            </div>
        `);

        if (SVP._cssNotLoadedClass) {
            domUtils.addClass(controls, SVP._cssNotLoadedClass);
            controls.style.visibility = 'hidden';
        }

        const controlsNode = Sizzle('.' + ClassNames.CONTROLS, controls)[0];
        const playButton = this.uiPlayButton.createElement();
        const timelineControls = this.uiTimelineControls.createElement();
        const languageButton = this.uiLanguageButton.createElement();
        const fullscreenButton = this.uiFullscreenButton.createElement();

        controlsNode.appendChild(playButton);
        controlsNode.appendChild(timelineControls);

        if (this._includeVolumeControls()) {
            controlsNode.appendChild(this.uiVolumeControls.createElement());
        }

        controlsNode.appendChild(languageButton);
        controlsNode.appendChild(fullscreenButton);

        domUtils.addClass(fullscreenButton, UIConstants.PROPERTY_CLASS.isLast);

        return controls;
    }

    _includeVolumeControls() {
        return !device.isIOS();
    }

    buildVideoUI() {
        const tabFocusElement = domUtils.fromString('<div class="svp_ui-tabfocus ' + UIConstants.FOCUS_ELEMENT_CLASS + '" tabindex="-1" aria-label="Videospelare">Videospelare</div>');
        const controlsElement = this._createVideoControls();

        this.api.element().appendChild(tabFocusElement);
        this.api.element().appendChild(controlsElement);

        this._setTabIndex(false);
        const uiContainer = this._getElement(ClassNames.UI_CONTAINER);

			if (!this.api.hasSplash()) {
				domUtils.removeClass(Sizzle('.' + STATE_CLASS.initialUi, uiContainer), STATE_CLASS.initialUi);
				domUtils.addClass(uiContainer, STATE_CLASS.visible);
			}
		}

    _setTabIndex(shouldSet) {
        const controlsContainer = this._getElement(ClassNames.CONTROLS);
        const index = shouldSet ? '0' : '-1';

        let items;
        if (controlsContainer) {
            items = Sizzle('.' + ClassNames.TOGGLE_TAB_INDEX, controlsContainer);
            for (let i = 0; i < items.length; i++) {
                items[i].setAttribute('tabindex', index);
            }
        }
    }

    fadeOutFn(force) {

        const uiContainer = this._getElement(ClassNames.UI_CONTAINER);
        if (!uiContainer || !this.api.data() || !this.api.data().allowNoUi) {
            return;
        }

        if (force || (!this._isVideoPaused && !this._isVideoBuffering && !this._isTouchDown && !this._controlsHasFocus && !this.uiLanguageButton.dialogVisible)) {
            this._setTabIndex(false);
            this.uiLanguageButton.destroyDialog();
            domUtils.removeClass(uiContainer, STATE_CLASS.visible);
            this._isControlsVisible = false;
            this.api.fire('controls', { visible: false });
            domUtils.addClass(uiContainer, UIConstants.PROPERTY_CLASS.hideCursor);
        }
    }

    fadeInFn(fadeOutInterval = FadeIntervals.MEDIUM) {
        clearTimeout(this._uiFadeTimeout);
        const uiContainer = this._getElement(ClassNames.UI_CONTAINER);
        if (!uiContainer) {
            return;
        }

        this._setTabIndex(true);
        domUtils.removeClass(uiContainer, UIConstants.PROPERTY_CLASS.hideCursor);
        domUtils.addClass(uiContainer, STATE_CLASS.visible);
        this._isControlsVisible = true;
        this.api.fire('controls', { visible: true });

        this._uiFadeTimeout = setTimeout(() => this.fadeOutFn(), fadeOutInterval);
    }

    _setPlayingState(state, what) {
        if (state === PLAYING_STATE.error) {
            this.uiPlayButton.setState('error', what);
        } else {
            if (!this._isVideoStarted && state === PLAYING_STATE.playing) {
                domUtils.removeClass(Sizzle('.' + STATE_CLASS.initialUi, Sizzle('.' + ClassNames.UI_CONTAINER, this.api.element())[0]), STATE_CLASS.initialUi);
                this._isVideoStarted = true;
                this.fadeInFn();
            }

            this._isVideoPlaying = state === PLAYING_STATE.playing;
            this._isVideoPaused = state === PLAYING_STATE.paused;
            this._isVideoBuffering = state === PLAYING_STATE.loading;

            const loader = Sizzle('.' + ClassNames.LOADER, this.api.element())[0];
            if (this._isVideoStarted && this._isVideoBuffering) {
                domUtils.addClass(loader, 'svp_ui-loader--loading');
            } else {
                domUtils.removeClass(loader, 'svp_ui-loader--loading');

                //hide ui if we've been in buffer state but no longer is in mouse over mode
                if (!this._isMouseOver && this._isVideoStarted) {
                    clearTimeout(this._uiFadeTimeout);
                    this._uiFadeTimeout = setTimeout(() => this.fadeOutFn(), FadeIntervals.MEDIUM);
                }
            }

            if (!this._isVideoBuffering) {
                this._prevState = state;
            }
        }
    }

    resetUi(clearAll) {
        clearTimeout(this._uiFadeTimeout);

        this._isVideoStarted = false;
        this._isVideoBuffering = false;
        this._uiFadeTimeout = null;

        const controlsContainer = this._getElement(ClassNames.CONTROLS);
        domUtils.removeClass(controlsContainer, STATE_CLASS.error);

			if (this.api.hasSplash()) {
				domUtils.addClass(controlsContainer, STATE_CLASS.hidden);
			}

        this.uiTimelineControls.reset();
        this.uiPlayButton.reset();

        if (clearAll) {
            this.uiLanguageButton.reset();
        }

        domUtils.addClass(controlsContainer, STATE_CLASS.initialUi);

        if (this.api.hasSplash()) {
            this.fadeOutFn(true);
            this.api.once(NAMESPACE + '-bind-reset.playing', () => {
                const controlsContainer = this._getElement(ClassNames.CONTROLS);
                domUtils.removeClass(controlsContainer, STATE_CLASS.hidden);
            });
        }
    }

    removeButton(button) {
        if (!this._insertedButtons.has(button)) {
            Debug.warn('Button was not found', button);

            return;
        }

        button.parentNode.removeChild(button);
        this._insertedButtons.delete(button);
    }


    insertButton(button, options) {
        if (this._insertedButtons.has(button)) {
            Debug.warn('Button was already inserted', button);

            return;
        }

        this._insertedButtons.add(button);

        if (!options || options.container === 'top-right') {
            const buttonRow = Sizzle('.' + ClassNames.BUTTON_CONTAINER_RIGHT, this.api.element())[0];

            if (!options || options.defaultStyle !== false) {
                domUtils.addClass(button, ClassNames.BUTTON_CONTAINER_BUTTON);
            }

            buttonRow.insertBefore(button, buttonRow.firstChild);
        } else if (options.container === 'top-left') {
            const buttonRow = Sizzle('.' + ClassNames.BUTTON_CONTAINER_LEFT, this.api.element())[0];

            if (options.defaultStyle !== false) {
                domUtils.addClass(button, ClassNames.BUTTON_CONTAINER_BUTTON);
            }

            buttonRow.appendChild(button);
        } else if (options.container === 'controls') {
            const controlsContainer = this._getElement(ClassNames.CONTROLS);
            controlsContainer.insertBefore(button, controlsContainer.children[controlsContainer.children.length - 1]);
        } else {
            this._insertedButtons.delete(button);

            Debug.warn('Unknown options.container when inserting button', (options && options.container));
        }
    }

    _addVideoEventListeners() {
        this.api.on(NAMESPACE + '.play', () => {
            if (this.api.config.autoFocusOnPlay && !this._isVideoStarted) {
                const tabFocusElement = Sizzle('.' + UIConstants.FOCUS_ELEMENT_CLASS, this.api.element())[0];
                if (tabFocusElement) {
                    tabFocusElement.focus();
                }
            }

            this._setPlayingState(PLAYING_STATE.loading);
        });
        this.api.on(NAMESPACE + '.buffering', () => this._setPlayingState(PLAYING_STATE.loading));
        this.api.on(NAMESPACE + '.seeking', () => this._setPlayingState(PLAYING_STATE.loading));
        this.api.on(NAMESPACE + '.canplay', () => this._setPlayingState(PLAYING_STATE.paused));
        this.api.on(NAMESPACE + '.seeked', () => this._setPlayingState(this._prevState));

        this.api.on(NAMESPACE + '.playing', () => {
            this._setPlayingState(PLAYING_STATE.playing);

            if (this.api.hasSplash()) {
                const buttonRow = Sizzle('.' + ClassNames.BUTTON_CONTAINER, this.api.element())[0];
                domUtils.addClass(buttonRow, ClassNames.BUTTON_CONTAINER_WAS_SPLASH);
            }

            // fade timeout is cleared on reset, meaning we need to re-set it if we're playing multiple videos in a row
            if (!this._uiFadeTimeout) {
                this._uiFadeTimeout = setTimeout(() => this.fadeOutFn(), FadeIntervals.SHORT);
            }

            const buttonRow = Sizzle('.' + ClassNames.BUTTON_CONTAINER_LEFT, this.api.element())[0];

            domUtils.removeClass(buttonRow, ClassNames.BUTTON_CONTAINER_LEFT_LIVE);
        });

        this.api.on(NAMESPACE + '.pause', () => {
            this._setPlayingState(PLAYING_STATE.paused);
            this.fadeInFn();
        });

        this.api.on(NAMESPACE + '.metadata', ({ detail }) => {
            if (Boolean(detail.live) === false) {
                return;
            }

            const buttonRow = Sizzle('.' + ClassNames.BUTTON_CONTAINER_LEFT, this.api.element())[0];
            domUtils.addClass(buttonRow, ClassNames.BUTTON_CONTAINER_LEFT_LIVE);
        });
    }

    _setupControlEvents() {
        const togglePlayFn = (e) => {
            e.preventDefault();

            this.uiPlayButton.togglePlay();

            return false;
        };

        const toggleUiFn = () => {
            if (this._isControlsVisible) {
                this.fadeOutFn();
            } else {
                this.fadeInFn();
            }
        };

        domUtils.addEvents(this._getElement(ClassNames.UI_CONTAINER), 'click', (e) => togglePlayFn(e), NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.UI_CONTAINER), 'touchend', (e) => {
            this._isTouchDown = false;

            if (e.cancelable) {
               e.preventDefault();
            }

            if (this._isTouchMove) {
                this._isTouchMove = false;

                return;
            }

            this._controlsHasFocus = false;
            toggleUiFn();
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.UI_CONTAINER), 'touchmove', () => {
            this._isTouchMove = false;
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.CONTROLS), 'touchstart', () => {
            this._isTouchClick = true;
            this._isTouchDown = true;
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.CONTROLS), 'touchend', () => {
            this._isTouchDown = false;
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.CONTROLS), 'click', (e) => {
            if (this._isTouchClick) {
                toggleUiFn(e);
            } else {
                togglePlayFn(e);
            }
            this._isTouchClick = false;
        }, NAMESPACE);

        domUtils.addEvents(this.api.element(), 'mousedown', () => {
            this.api.element().setAttribute('tabindex', '0');
            this._getElement(UIConstants.FOCUS_ELEMENT_CLASS).removeAttribute('tabindex');
        }, NAMESPACE);

        domUtils.addEvents(this.api.element(), 'blur', () => {
            this.api.element().removeAttribute('tabindex');
            this._getElement(UIConstants.FOCUS_ELEMENT_CLASS).setAttribute('tabindex', '0');
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.CONTROLS), 'focusin', () => {
            this._controlsHasFocus = true;
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.BUTTON_CONTAINER), 'focusin', () => {
            this._controlsHasFocus = true;
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.BUTTON_CONTAINER), 'focusout', () => {
            this._controlsHasFocus = false;
            setTimeout(() => {
                if (!this._controlsHasFocus) {
                    this.fadeOutFn();
                }
            }, 250);
        }, NAMESPACE);

        domUtils.addEvents(this._getElement(ClassNames.CONTROLS), 'focusout', () => {
            this._controlsHasFocus = false;
            setTimeout(() => {
                if (!this._controlsHasFocus) {
                    this.fadeOutFn();
                }
            }, 250);
        }, NAMESPACE);
    }

    _bindUiInteractionEvents() {
        if (!device.isTouch()) {
            this.api.element().addEventSVP('mousemove', () => {
                this._isMouseOver = true;
                if (this._isVideoStarted) {
                    this.fadeInFn(FadeIntervals.MEDIUM);
                }
            });

            this.api.element().addEventSVP('mouseout', () => {
                this._isMouseOver = false;
                if (this._isVideoStarted) {
                    clearTimeout(this._uiFadeTimeout);
                    this._uiFadeTimeout = setTimeout(() => this.fadeOutFn(), FadeIntervals.SHORT);
                }
            });
        }

        this._setupControlEvents();

        this.uiPlayButton.bindEvents(this.api);
        this.uiFullscreenButton.bindEvents(this.api);
        this.uiTimelineControls.bindEvents(this.api);
        this.uiLanguageButton.bindEvents(this.api);

        if (this._includeVolumeControls()) {
            this.uiVolumeControls.bindEvents(this.api);
        }

        this.api.on('showControls', (e) => {
            if (this._isVideoStarted) {
                if (e.detail.shouldShow) {
                    this.fadeInFn();
                } else {
                    this.fadeOutFn();
                }
            }
        });

        this.api.on('languageDialog', (e) => {
            const uiContainer = this._getElement(ClassNames.UI_CONTAINER);
            if (e.detail.visible) {
                domUtils.addClass(uiContainer, 'svp_ui--language-dialog-visible');
            } else {
                domUtils.removeClass(uiContainer, 'svp_ui--language-dialog-visible');
            }
        });
    }

    bindEvents() {
        if (!this._isEventsBound) {
            this._addVideoEventListeners();
            this._bindUiInteractionEvents();

            this.api.on(NAMESPACE + '.ready', (e) => {
                this._useExternalUi = e.detail.externalUi;
            });

            this._isEventsBound = true;
        }

        this.api.once(NAMESPACE + '-bind.playing', () => {
            const controlsContainer = this._getElement(ClassNames.CONTROLS);
            domUtils.removeClass(controlsContainer, STATE_CLASS.hidden);
            const uiContainer = Sizzle('.' + ClassNames.UI_CONTAINER, this.api.element())[0];

            if (this._useExternalUi) {
                // TODO this 'else'-clause is for chromecast-with-HDS-video, and should be removed once the flash player isn't used any more.
                if (this.api.data().allowNoUi) {
                    domUtils.addClass(uiContainer, STATE_CLASS.externalUi);
                } else {
                    domUtils.addClass(Sizzle('.' + ClassNames.CONTROLS, this.api.element())[0], STATE_CLASS.animatable);
                    this.fadeInFn();
                }
            } else {
                domUtils.removeClass(uiContainer, STATE_CLASS.externalUi);
                domUtils.addClass(controlsContainer, STATE_CLASS.animatable);
            }
        });
    }

    destroy() {
        const element = Sizzle('.' + ClassNames.UI_CONTAINER, this.api.element())[0];
        const tabFocusElement = Sizzle('.' + UIConstants.FOCUS_ELEMENT_CLASS, this.api.element())[0];

        if (element) {
            UIPlayButton.destroy(this.api);
            UITimelineControls.destroy(this.api);
            UILanguageButton.destroy(this.api);
            UIFullscreenButton.destroy(this.api);
            UIVolumeControls.destroy(this.api);

            element.parentElement.removeChild(element);
            tabFocusElement.parentElement.removeChild(tabFocusElement);
        }
    }
}

module.exports = {
	namespace: NAMESPACE,
	create: function (api) {
		if (!api.config.controls) {
			return;
		}

		const ui = new UIPlugin(api);

        ui.buildVideoUI();

		api.filters.push({
			load: function (videoId, autoplay) {
                ui.resetUi(true);
                ui.bindEvents();

				if (api.hasSplash()) {
					ui.fadeOutFn(true);
				}

				return false;
			},

			insertButton: function (button, options) {
				ui.insertButton(button, options);
			},

            removeButton: function (button) {
			    ui.removeButton(button);
            },

			reset: function () {
				ui.resetUi(false);
			}
		});

        instances.set(api, ui);

	},
	destroy: function (api) {
	    if (!instances.has(api)) {
	        return;
        }

        const instance = instances.get(api);
	    instance.destroy();
	    instances.delete(api);
	}
};



// WEBPACK FOOTER //
// ./src/js/plugins/ui.js